import React, { useCallback, useEffect, useRef, useState } from 'react';

interface LatLng {
  lat: number;
  lng: number;
}

interface DeliveryZoneMapProps {
  initialPolygon?: LatLng[] | null;
  centerCoordinates?: LatLng;
  onPolygonChange: (polygon: LatLng[] | null) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

declare global {
  interface Window {
    google: any;
  }
}

const DEFAULT_CENTER: LatLng = { lat: 45.8992, lng: 6.1294 };

const POLYGON_STYLE = {
  strokeColor: '#2563eb',
  strokeOpacity: 0.9,
  strokeWeight: 2,
  fillColor: '#3b82f6',
  fillOpacity: 0.2,
};

type DrawMode = 'empty' | 'drawing' | 'done';

export const DeliveryZoneMap: React.FC<DeliveryZoneMapProps> = ({
  initialPolygon,
  centerCoordinates,
  onPolygonChange,
  disabled = false,
  readOnly = false,
}) => {
  const isDisabled = disabled || readOnly;

  const mapRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<any>(null);
  const polygonRef = useRef<any>(null);
  const clickListenerRef = useRef<any>(null);
  const initializedRef = useRef(false);

  // Keep latest props in refs so the imperative map callbacks stay stable
  // and re-renders/emits never rebuild the map (which would break drawing).
  const onPolygonChangeRef = useRef(onPolygonChange);
  const isDisabledRef = useRef(isDisabled);
  useEffect(() => {
    onPolygonChangeRef.current = onPolygonChange;
    isDisabledRef.current = isDisabled;
  });

  const [mapReady, setMapReady] = useState<boolean>(() => Boolean(window.google?.maps));
  const [mode, setMode] = useState<DrawMode>('empty');
  const [pointCount, setPointCount] = useState(0);

  const emitPolygonChange = useCallback(() => {
    const poly = polygonRef.current;
    if (!poly) {
      setPointCount(0);
      onPolygonChangeRef.current(null);
      return;
    }
    const path = poly.getPath();
    const vertices: LatLng[] = [];
    for (let i = 0; i < path.getLength(); i++) {
      const point = path.getAt(i);
      vertices.push({ lat: point.lat(), lng: point.lng() });
    }
    setPointCount(vertices.length);
    onPolygonChangeRef.current(vertices.length < 3 ? null : vertices);
  }, []);

  const attachPolygonListeners = useCallback(
    (polygon: any) => {
      const events = window.google.maps.event;
      const path = polygon.getPath();
      events.addListener(path, 'set_at', emitPolygonChange);
      events.addListener(path, 'insert_at', emitPolygonChange);
      events.addListener(path, 'remove_at', emitPolygonChange);
      events.addListener(polygon, 'dragend', emitPolygonChange);
    },
    [emitPolygonChange]
  );

  const clearPolygon = useCallback(() => {
    if (polygonRef.current) {
      polygonRef.current.setMap(null);
      polygonRef.current = null;
    }
  }, []);

  const stopDrawing = useCallback(() => {
    if (clickListenerRef.current) {
      window.google.maps.event.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }
  }, []);

  const renderExistingPolygon = useCallback(
    (points: LatLng[]) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      clearPolygon();

      const editable = !isDisabledRef.current;
      const poly = new window.google.maps.Polygon({
        map,
        paths: points,
        editable,
        draggable: editable,
        ...POLYGON_STYLE,
      });
      polygonRef.current = poly;

      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds);

      if (editable) {
        attachPolygonListeners(poly);
      }
      setPointCount(points.length);
      setMode('done');
    },
    [attachPolygonListeners, clearPolygon]
  );

  const startDrawing = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    stopDrawing();
    clearPolygon();

    // Build the polygon around an explicit (empty) MVCArray path. A polygon
    // constructed with `paths: []` has no path at all, so `getPath()` returns
    // undefined and attaching listeners to it throws ("__e3_" of undefined).
    const path = new window.google.maps.MVCArray();
    const poly = new window.google.maps.Polygon({
      map,
      paths: [path],
      editable: true,
      draggable: true,
      ...POLYGON_STYLE,
    });
    polygonRef.current = poly;
    attachPolygonListeners(poly);

    setMode('drawing');
    setPointCount(0);
    onPolygonChangeRef.current(null);

    clickListenerRef.current = map.addListener('click', (event: any) => {
      path.push(event.latLng); // fires insert_at -> emitPolygonChange
    });
  }, [attachPolygonListeners, clearPolygon, stopDrawing]);

  // 1. Load the Google Maps script once (shared across all instances).
  useEffect(() => {
    if (window.google?.maps) {
      setMapReady(true);
      return;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      // eslint-disable-next-line no-console
      console.warn('VITE_GOOGLE_MAPS_API_KEY is not set; DeliveryZoneMap will not render a map.');
      return;
    }

    let cancelled = false;
    const onLoaded = () => {
      if (!cancelled) setMapReady(true);
    };

    const existing = document.getElementById('google-maps-script') as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', onLoaded);
      return () => {
        cancelled = true;
        existing.removeEventListener('load', onLoaded);
      };
    }

    const scriptEl = document.createElement('script');
    scriptEl.id = 'google-maps-script';
    scriptEl.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
    scriptEl.async = true;
    scriptEl.defer = true;
    scriptEl.addEventListener('load', onLoaded);
    document.body.appendChild(scriptEl);

    return () => {
      cancelled = true;
      scriptEl.removeEventListener('load', onLoaded);
    };
  }, []);

  // 2. Create the map, and draw the saved polygon once it (asynchronously) arrives.
  const initialPolygonSig = initialPolygon ? JSON.stringify(initialPolygon) : '';
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: centerCoordinates || DEFAULT_CENTER,
        zoom: 14,
        disableDefaultUI: false,
      });
    }

    if (!initializedRef.current && initialPolygon && initialPolygon.length >= 3) {
      initializedRef.current = true;
      renderExistingPolygon(initialPolygon);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, initialPolygonSig, renderExistingPolygon]);

  // 3. Recenter when coordinates load after the map, as long as nothing is drawn.
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !centerCoordinates) return;
    if (polygonRef.current) return;
    mapInstanceRef.current.setCenter(centerCoordinates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, centerCoordinates?.lat, centerCoordinates?.lng]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      stopDrawing();
      clearPolygon();
    };
  }, [stopDrawing, clearPolygon]);

  const beginDrawing = () => {
    initializedRef.current = true; // a saved polygon arriving later must not overwrite a fresh draw
    startDrawing();
  };

  const handleFinish = () => {
    stopDrawing();
    setMode('done');
  };

  return (
    <div className="space-y-2">
      <div ref={mapRef} className="w-full h-80 rounded-md border border-gray-200" />
      {!isDisabled && (
        <div className="flex flex-wrap items-center gap-2">
          {mode === 'empty' && (
            <>
              <span className="text-xs text-gray-500">
                Click “Draw delivery zone”, then click on the map to add boundary points.
              </span>
              <button
                type="button"
                onClick={beginDrawing}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Draw delivery zone
              </button>
            </>
          )}

          {mode === 'drawing' && (
            <>
              <span className="text-xs text-gray-500">
                Click the map to add points ({pointCount}). Add at least 3, then finish.
              </span>
              <button
                type="button"
                onClick={handleFinish}
                disabled={pointCount < 3}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-white bg-alps-primary hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Finish
              </button>
              <button
                type="button"
                onClick={beginDrawing}
                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Clear
              </button>
            </>
          )}

          {mode === 'done' && (
            <button
              type="button"
              onClick={beginDrawing}
              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Redraw delivery zone
            </button>
          )}
        </div>
      )}
    </div>
  );
};
